
import { useState } from 'react'
import useSWR from 'swr'
import {
  Network, Plus, Trash2, RefreshCw, ExternalLink,
  Globe, Server, Edit2, Check, X, AlertCircle,
  Wifi, WifiOff, ChevronDown, ChevronUp, Info,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

// ── Types matching the CF Connectivity Directory Services API ──────────────
interface CFHost {
  ipv4?: string
  ipv6?: string
  hostname?: string
  network?: {
    tunnel_id?: string
    resolver_network?: { tunnel_id?: string }
  }
  resolver_network?: { tunnel_id?: string }
}

interface CFService {
  service_id: string
  name: string
  type: 'http'
  host: CFHost
  http_port?: number | null
  https_port?: number | null
  created_at: string
  updated_at: string
}

interface CFResponse {
  success: boolean
  result: CFService[]
  result_info?: { count: number; page: number; per_page: number; total_count: number }
  errors?: { code: number; message: string }[]
}

// ── Helpers ───────────────────────────────────────────────────────────────
const fetcher = (url: string) => fetch(url).then(r => r.json())

function resolveHostDisplay(host: CFHost): string {
  if (host.hostname) return host.hostname
  if (host.ipv4 && host.ipv6) return `${host.ipv4} / ${host.ipv6}`
  if (host.ipv4) return host.ipv4
  if (host.ipv6) return host.ipv6
  return '—'
}

function resolveTunnelId(host: CFHost): string | null {
  return (
    host.network?.tunnel_id ??
    host.network?.resolver_network?.tunnel_id ??
    host.resolver_network?.tunnel_id ??
    null
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// ── Host type discriminator for the form ─────────────────────────────────
type HostMode = 'ipv4' | 'ipv6' | 'hostname'

const BLANK_FORM = {
  name: '',
  type: 'http' as const,
  hostMode: 'hostname' as HostMode,
  ipv4: '',
  ipv6: '',
  hostname: '',
  tunnelId: '',
  httpPort: '',
  httpsPort: '',
}

// ── Service Card ──────────────────────────────────────────────────────────
function ServiceCard({
  service,
  onDelete,
  onEdit,
}: {
  service: CFService
  onDelete: (id: string, name: string) => void
  onEdit: (service: CFService) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const tunnelId = resolveTunnelId(service.host)
  const hostDisplay = resolveHostDisplay(service.host)

  return (
    <Card className="bg-card border-border hover:border-lava/20 transition-colors group">
      <CardContent className="p-0">
        {/* Main row */}
        <div className="flex items-center gap-3 px-4 py-3">
          {/* Status dot + icon */}
          <div className="relative flex-shrink-0">
            <div className="p-2 rounded-md bg-cyan-accent/10 border border-cyan-accent/20">
              <Network className="w-4 h-4 text-cyan-accent" />
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-success border border-card" />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground truncate">{service.name}</span>
              <Badge variant="outline" className="text-[9px] px-1.5 h-4 border-border font-mono text-muted-foreground flex-shrink-0">
                {service.type.toUpperCase()}
              </Badge>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <Globe className="w-3 h-3 text-muted-foreground flex-shrink-0" />
              <code className="text-[10px] font-mono text-muted-foreground truncate">{hostDisplay}</code>
              {(service.http_port || service.https_port) && (
                <>
                  <Separator orientation="vertical" className="h-3" />
                  <span className="text-[10px] text-muted-foreground">
                    {[service.http_port && `:${service.http_port}`, service.https_port && `:${service.https_port} (TLS)`]
                      .filter(Boolean).join(' / ')}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Ports */}
          <div className="hidden md:flex items-center gap-1.5 flex-shrink-0">
            {service.http_port && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Badge variant="outline" className="text-[9px] font-mono px-1.5 h-5 border-border text-muted-foreground">
                      HTTP :{service.http_port}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs">HTTP Port</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {service.https_port && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Badge variant="outline" className="text-[9px] font-mono px-1.5 h-5 border-success/30 text-success bg-success/5">
                      TLS :{service.https_port}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs">HTTPS Port</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          {/* Created */}
          <span className="hidden lg:block text-[10px] text-muted-foreground flex-shrink-0 font-mono">
            {formatDate(service.created_at)}
          </span>

          {/* Actions */}
          <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={() => onEdit(service)}
            >
              <Edit2 className="w-3.5 h-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              onClick={() => onDelete(service.service_id, service.name)}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>

          {/* Expand toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground flex-shrink-0"
            onClick={() => setExpanded(v => !v)}
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </Button>
        </div>

        {/* Expanded detail */}
        {expanded && (
          <>
            <Separator className="bg-border" />
            <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground/60 font-medium mb-1">Service ID</p>
                <code className="text-[10px] font-mono text-foreground break-all">{service.service_id}</code>
              </div>
              {tunnelId && (
                <div>
                  <p className="text-[9px] uppercase tracking-wider text-muted-foreground/60 font-medium mb-1">Tunnel ID</p>
                  <code className="text-[10px] font-mono text-lava">{tunnelId}</code>
                </div>
              )}
              <div>
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground/60 font-medium mb-1">Created</p>
                <span className="text-[10px] text-foreground">{formatDate(service.created_at)}</span>
              </div>
              <div>
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground/60 font-medium mb-1">Updated</p>
                <span className="text-[10px] text-foreground">{formatDate(service.updated_at)}</span>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ── Service Form Dialog ────────────────────────────────────────────────────
function ServiceFormDialog({
  open,
  onOpenChange,
  initial,
  onSave,
  saving,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  initial?: typeof BLANK_FORM
  onSave: (form: typeof BLANK_FORM) => void
  saving: boolean
}) {
  const [form, setForm] = useState<typeof BLANK_FORM>(initial ?? BLANK_FORM)

  function set(k: keyof typeof BLANK_FORM, v: string) {
    setForm(f => ({ ...f, [k]: v }))
  }

  const isEditing = !!initial

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2">
            <Network className="w-4 h-4 text-lava" />
            {isEditing ? 'Update Connectivity Service' : 'Create Connectivity Service'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Define a network host reachable via Cloudflare&apos;s connectivity directory. Requests route through your tunnel.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-1">
          {/* Name */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Service Name</Label>
            <Input
              value={form.name}
              onChange={e => set('name', e.target.value)}
              className="h-8 text-xs"
              placeholder="web-app"
            />
          </div>

          {/* Type (fixed: http) */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Type</Label>
            <div className="h-8 px-3 flex items-center rounded-md border border-border bg-muted text-xs font-mono text-muted-foreground">
              http
            </div>
          </div>

          {/* Host mode selector */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Host Type</Label>
            <Select value={form.hostMode} onValueChange={v => set('hostMode', v as HostMode)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hostname" className="text-xs">Hostname</SelectItem>
                <SelectItem value="ipv4" className="text-xs">IPv4 Address</SelectItem>
                <SelectItem value="ipv6" className="text-xs">IPv6 Address</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Conditional host field */}
          {form.hostMode === 'hostname' && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Hostname</Label>
              <Input
                value={form.hostname}
                onChange={e => set('hostname', e.target.value)}
                className="h-8 text-xs font-mono"
                placeholder="api.example.com"
              />
            </div>
          )}
          {form.hostMode === 'ipv4' && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">IPv4 Address</Label>
              <Input
                value={form.ipv4}
                onChange={e => set('ipv4', e.target.value)}
                className="h-8 text-xs font-mono"
                placeholder="10.0.0.1"
              />
            </div>
          )}
          {form.hostMode === 'ipv6' && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">IPv6 Address</Label>
              <Input
                value={form.ipv6}
                onChange={e => set('ipv6', e.target.value)}
                className="h-8 text-xs font-mono"
                placeholder="2001:db8::1"
              />
            </div>
          )}

          {/* Tunnel ID */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium flex items-center gap-1.5">
              Tunnel ID
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger><Info className="w-3 h-3 text-muted-foreground" /></TooltipTrigger>
                  <TooltipContent className="text-xs max-w-xs">
                    The Cloudflare Tunnel (or Resolver Network) ID that routes traffic to this host.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Input
              value={form.tunnelId}
              onChange={e => set('tunnelId', e.target.value)}
              className="h-8 text-xs font-mono"
              placeholder="0191dce4-9ab4-7fce-b660-8e5dec5172da"
            />
          </div>

          {/* Ports */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">HTTP Port</Label>
              <Input
                type="number"
                value={form.httpPort}
                onChange={e => set('httpPort', e.target.value)}
                className="h-8 text-xs font-mono"
                placeholder="8080"
                min={1}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">HTTPS Port</Label>
              <Input
                type="number"
                value={form.httpsPort}
                onChange={e => set('httpsPort', e.target.value)}
                className="h-8 text-xs font-mono"
                placeholder="8443"
                min={1}
              />
            </div>
          </div>

          <Button
            onClick={() => onSave(form)}
            disabled={saving || !form.name.trim()}
            className="w-full h-8 lava-gradient text-lava-foreground border-0 text-xs hover:opacity-90"
          >
            {saving
              ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1.5" />
              : <Check className="w-3.5 h-3.5 mr-1.5" />
            }
            {isEditing ? 'Update Service' : 'Create Service'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ── Build CF API body from form ───────────────────────────────────────────
function buildHostBody(form: typeof BLANK_FORM) {
  const tunnelId = form.tunnelId.trim()

  if (form.hostMode === 'hostname') {
    return {
      hostname: form.hostname.trim(),
      ...(tunnelId ? { resolver_network: { tunnel_id: tunnelId } } : {}),
    }
  }

  const network = tunnelId ? { tunnel_id: tunnelId } : undefined
  if (form.hostMode === 'ipv4') return { ipv4: form.ipv4.trim(), ...(network ? { network } : {}) }
  return { ipv6: form.ipv6.trim(), ...(network ? { network } : {}) }
}

// ── Page ──────────────────────────────────────────────────────────────────
export default function ConnectivityPage() {
  const { data, mutate, isLoading } = useSWR<CFResponse>('/api/connectivity', fetcher, {
    refreshInterval: 30_000,
  })

  const [createOpen, setCreateOpen] = useState(false)
  const [editService, setEditService] = useState<CFService | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const services = data?.result ?? []
  const cfErrors = data?.errors ?? []
  const totalCount = data?.result_info?.total_count ?? services.length

  async function handleCreate(form: typeof BLANK_FORM) {
    setSaving(true)
    try {
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        type: 'http',
        host: buildHostBody(form),
      }
      if (form.httpPort) body.http_port = parseInt(form.httpPort)
      if (form.httpsPort) body.https_port = parseInt(form.httpsPort)

      const res = await fetch('/api/connectivity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (res.ok) {
        setCreateOpen(false)
        mutate()
      }
    } finally {
      setSaving(false)
    }
  }

  async function handleUpdate(form: typeof BLANK_FORM) {
    if (!editService) return
    setSaving(true)
    try {
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        type: 'http',
        host: buildHostBody(form),
      }
      if (form.httpPort) body.http_port = parseInt(form.httpPort)
      if (form.httpsPort) body.https_port = parseInt(form.httpsPort)

      const res = await fetch(`/api/connectivity/${editService.service_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (res.ok) {
        setEditService(null)
        mutate()
      }
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await fetch(`/api/connectivity/${deleteTarget.id}`, { method: 'DELETE' })
      setDeleteTarget(null)
      mutate()
    } finally {
      setDeleting(false)
    }
  }

  function openEdit(service: CFService) {
    const tunnelId = resolveTunnelId(service.host) ?? ''
    const hostMode: HostMode =
      service.host.hostname ? 'hostname'
      : service.host.ipv6 ? 'ipv6'
      : 'ipv4'

    setEditService(service)
    // editService drives the pre-filled form through initial prop
  }

  function buildEditForm(s: CFService): typeof BLANK_FORM {
    const tunnelId = resolveTunnelId(s.host) ?? ''
    const hostMode: HostMode =
      s.host.hostname ? 'hostname' : s.host.ipv6 ? 'ipv6' : 'ipv4'
    return {
      name: s.name,
      type: 'http',
      hostMode,
      ipv4: s.host.ipv4 ?? '',
      ipv6: s.host.ipv6 ?? '',
      hostname: s.host.hostname ?? '',
      tunnelId,
      httpPort: s.http_port ? String(s.http_port) : '',
      httpsPort: s.https_port ? String(s.https_port) : '',
    }
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-md bg-cyan-accent/10">
            <Network className="w-4 h-4 text-cyan-accent" />
          </div>
          <div>
            <h2 className="text-base font-bold text-foreground">Connectivity Services</h2>
            <p className="text-xs text-muted-foreground">
              {isLoading ? 'Loading...' : `${totalCount} service${totalCount !== 1 ? 's' : ''} · Cloudflare Connectivity Directory`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 text-xs border-border"
            onClick={() => mutate()}
          >
            <RefreshCw className="w-3 h-3" />
            Refresh
          </Button>
          <Button
            size="sm"
            className="gap-2 lava-gradient text-lava-foreground border-0 text-xs hover:opacity-90"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="w-3.5 h-3.5" />
            New Service
          </Button>
        </div>
      </div>

      {/* CF API errors */}
      {cfErrors.length > 0 && (
        <div className="flex items-start gap-2.5 p-3 rounded-lg bg-destructive/5 border border-destructive/20">
          <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            {cfErrors.map((e, i) => (
              <p key={i} className="text-xs text-destructive font-medium">
                [{e.code}] {e.message}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Services', value: isLoading ? '—' : totalCount, icon: Network, color: 'text-cyan-accent' },
          { label: 'HTTP Services', value: isLoading ? '—' : services.filter(s => s.type === 'http').length, icon: Globe, color: 'text-lava' },
          { label: 'With HTTPS Port', value: isLoading ? '—' : services.filter(s => s.https_port).length, icon: Wifi, color: 'text-success' },
          { label: 'With Tunnel', value: isLoading ? '—' : services.filter(s => resolveTunnelId(s.host)).length, icon: Server, color: 'text-warning' },
        ].map(stat => (
          <Card key={stat.label} className="bg-card border-border">
            <CardContent className="p-3 flex items-center gap-3">
              <div className={cn('p-2 rounded bg-muted/80 flex-shrink-0', stat.color)}>
                <stat.icon className="w-3.5 h-3.5" />
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground leading-tight">{stat.label}</p>
                <p className="text-lg font-bold text-foreground tabular-nums">{String(stat.value)}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Services list */}
      <div className="space-y-2">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-md bg-muted animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-32 bg-muted rounded animate-pulse" />
                    <div className="h-2 w-48 bg-muted rounded animate-pulse" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : services.length === 0 ? (
          <Card className="border-dashed border-border bg-card">
            <CardContent className="py-14 text-center">
              <WifiOff className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-foreground font-medium">No connectivity services</p>
              <p className="text-xs text-muted-foreground mt-1 max-w-xs mx-auto">
                Create a service to register a network host in your Cloudflare connectivity directory.
              </p>
              <Button
                size="sm"
                className="mt-4 gap-2 lava-gradient text-lava-foreground border-0 text-xs hover:opacity-90"
                onClick={() => setCreateOpen(true)}
              >
                <Plus className="w-3.5 h-3.5" />
                Create First Service
              </Button>
            </CardContent>
          </Card>
        ) : (
          services.map(s => (
            <ServiceCard
              key={s.service_id}
              service={s}
              onDelete={(id, name) => setDeleteTarget({ id, name })}
              onEdit={(svc) => setEditService(svc)}
            />
          ))
        )}
      </div>

      {/* API reference card */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs flex items-center gap-2 text-muted-foreground">
            <ExternalLink className="w-3.5 h-3.5" />
            Cloudflare API — Connectivity Directory Services
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {[
            { method: 'GET',    color: 'text-success  bg-success/10  border-success/20',    path: `/rest/connectivity/directory/services`, note: 'List all services' },
            { method: 'POST',   color: 'text-cyan-accent bg-cyan-accent/10 border-cyan-accent/20', path: `/rest/connectivity/directory/services`, note: 'Create service' },
            { method: 'GET',    color: 'text-success  bg-success/10  border-success/20',    path: `/rest/connectivity/directory/services/:id`, note: 'Get service' },
            { method: 'PUT',    color: 'text-warning  bg-warning/10  border-warning/20',    path: `/rest/connectivity/directory/services/:id`, note: 'Update service' },
            { method: 'DELETE', color: 'text-destructive bg-destructive/10 border-destructive/20', path: `/rest/connectivity/directory/services/:id`, note: 'Delete service' },
          ].map((r, i) => (
            <div key={i} className="flex items-center gap-2.5 py-1.5 border-b border-border last:border-0">
              <Badge variant="outline" className={cn('text-[9px] font-mono px-1.5 h-4 border flex-shrink-0', r.color)}>
                {r.method}
              </Badge>
              <code className="text-[10px] font-mono text-muted-foreground flex-1">{r.path}</code>
              <span className="text-[10px] text-muted-foreground">{r.note}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <ServiceFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSave={handleCreate}
        saving={saving}
      />

      {/* Edit dialog */}
      {editService && (
        <ServiceFormDialog
          open={!!editService}
          onOpenChange={(v) => { if (!v) setEditService(null) }}
          initial={buildEditForm(editService)}
          onSave={handleUpdate}
          saving={saving}
        />
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null) }}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-sm">Delete Service</AlertDialogTitle>
            <AlertDialogDescription className="text-xs">
              Permanently delete <strong>{deleteTarget?.name}</strong> from your Cloudflare connectivity directory.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-8 text-xs">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="h-8 text-xs bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
